/**
 * NotificationBanner
 *
 * A dismissable in-app banner that asks the user to enable push notifications.
 * Shows only once per session, and only when:
 *   1. User is logged in
 *   2. Firebase is configured
 *   3. Browser notification permission is "default" (not yet asked or denied)
 */
import { useState, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';

export default function NotificationBanner() {
  const { user } = useAuth();
  const { requestPermission, isFirebaseConfigured } = useNotifications();
  const [visible,  setVisible]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [success,  setSuccess]  = useState(false);

  useEffect(() => {
    if (!user || !isFirebaseConfigured) return;
    if (!('Notification' in window)) return;

    // Only show if permission hasn't been decided yet
    if (Notification.permission === 'default') {
      // Small delay so it doesn't pop on initial page load
      const timer = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [user, isFirebaseConfigured]);

  const handleEnable = async () => {
    setLoading(true);
    const granted = await requestPermission();
    setLoading(false);
    if (granted) {
      setSuccess(true);
      setTimeout(() => setVisible(false), 2000);
    } else {
      setVisible(false); // dismissed or blocked — hide
    }
  };

  if (!visible) return null;

  return (
    <div style={s.banner}>
      <div style={s.icon}>🔔</div>
      <div style={s.content}>
        <div style={s.title}>
          {success ? '✅ Notifications Enabled!' : 'Stay Updated on Your Rentals'}
        </div>
        {!success && (
          <div style={s.sub}>
            Get instant push notifications when your order is approved, shipped, or delivered.
          </div>
        )}
      </div>
      {!success && (
        <>
          <button
            onClick={handleEnable}
            disabled={loading}
            style={s.enableBtn}
          >
            {loading ? '...' : 'Enable'}
          </button>
          <button onClick={() => setVisible(false)} style={s.dismissBtn} aria-label="Dismiss">
            ✕
          </button>
        </>
      )}
    </div>
  );
}

const s = {
  banner: {
    position:   'fixed',
    bottom:     80,
    left:       '50%',
    transform:  'translateX(-50%)',
    zIndex:     1000,
    display:    'flex',
    alignItems: 'center',
    gap:        12,
    background: 'var(--ink)',
    color:      'var(--cream)',
    padding:    '14px 20px',
    boxShadow:  '0 8px 40px rgba(14,11,8,0.4)',
    border:     '1px solid rgba(184,146,74,0.3)',
    minWidth:   300,
    maxWidth:   480,
    animation:  'fadeUp 0.3s ease',
  },
  icon: { fontSize: 22, flexShrink: 0 },
  content: { flex: 1 },
  title:   { fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', marginBottom: 2 },
  sub:     { fontSize: 10, color: 'rgba(245,240,232,0.55)', letterSpacing: '0.04em' },
  enableBtn: {
    background:     'var(--gold)',
    color:          'var(--ink)',
    border:         'none',
    padding:        '8px 16px',
    fontSize:       10,
    fontWeight:     700,
    fontFamily:     "'Josefin Sans', sans-serif",
    letterSpacing:  '0.12em',
    textTransform:  'uppercase',
    cursor:         'pointer',
    flexShrink:     0,
    transition:     'opacity 0.2s',
  },
  dismissBtn: {
    background:  'none',
    border:      'none',
    color:       'rgba(245,240,232,0.4)',
    cursor:      'pointer',
    fontSize:    16,
    padding:     '4px 6px',
    flexShrink:  0,
  }
};
